<template>
  <div>
    <Modal
      title="Edit Customer"
      className="customer-edit-modal"
      :saving="saving"
      @hide-modal="$emit('hide-modal')"
      @submit="handleSave"
    >
      <div>
        <div class="d-flex gap-3">
          <TextField
            v-model="formData.first_name"
            label="First Name"
            name="first_name"
            class="flex-1"
            :validation-errors="v$.$errors"
          />
          <TextField
            v-model="formData.last_name"
            label="Last Name"
            name="last_name"
            class="flex-1 icon-margin-right-2x"
            :validation-errors="v$.$errors"
          />
        </div>
        <div class="d-flex flex-column">
          <EmailFields class="full-width" :form-data="formData" />
          <Divider class="my-3" />
          <PhoneFields class="full-width" :form-data="formData" />
          <FeatureFlag feature="parent_sub_account_ff_enabled">
            <Divider class="my-3" />
            <div v-if="formData.has_subs">
              This customer has one or more sub-customers and can not have a
              parent assigned.
            </div>
            <div v-else>
              <ParentFields
                class="full-width"
                :form-data="formData"
                @update:parentId="
                  (value) => {
                    formData.parent ??= {};
                    formData.parent.id = value;
                  }
                "
                @update:delegateBilling="
                  (value) => {
                    formData.parent ??= {};
                    formData.parent.delegate_billing = value;
                  }
                "
              />
            </div>
          </FeatureFlag>
        </div>
      </div>
      <template #delete>
        <Button type="danger" @click="handleArchiveClicked">
          <span v-if="!loadingAction">{{ `${actionAvailable} Customer` }}</span>
          <Spinner v-else message="Saving"></Spinner>
        </Button>
      </template>
    </Modal>
    <ConfirmArchiveModal
      :open="confirmArchiveModalOpen"
      @archive-confirmed="handleArchiveConfirmed"
      @close="confirmArchiveModalOpen = false"
    />
    <ConfirmUnarchiveModal
      :open="confirmUnarchiveModalOpen"
      @close="confirmUnarchiveModalOpen = false"
    />
  </div>
</template>
<script>
import { mapActions } from "vuex";
import { EventBus } from "@/lib/event_bus";

import Button from "@/components/Button.vue";
import Divider from "@/components/Divider.vue";
import FeatureFlag from "@/components/FeatureFlag.vue";
import TextField from "@/components/TextField.vue";
import ConfirmArchiveModal from "@/Customer/ConfirmArchiveModal.vue";
import ConfirmUnarchiveModal from "@/Customer/ConfirmUnarchiveModal.vue";
import ParentFields from "@/Customer/CustomerModal/ParentFields.vue";
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import Modal from "../../components/Modal.vue";
import Spinner from "../../components/Spinner.vue";
import { cloneObj } from "../../lib/helpers.js";
import EmailFields from "./EmailFields.vue";
import PhoneFields from "./PhoneFields.vue";
export default {
  components: {
    ConfirmUnarchiveModal,
    ConfirmArchiveModal,
    ParentFields,
    FeatureFlag,
    Modal,
    TextField,
    EmailFields,
    PhoneFields,
    Spinner,
    Divider,
    Button
  },
  props: {
    customer: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ["hide-modal", "save-success"],
  setup() {
    return {
      v$: useVuelidate()
    };
  },
  data() {
    return {
      formData: cloneObj(this.customer),
      archiving: false,
      saving: false,
      confirm: false,
      loadingAction: false,
      confirmArchiveModalOpen: false,
      confirmUnarchiveModalOpen: false
    };
  },
  computed: {
    actionAvailable() {
      return this.customer.archived ? "Unarchive" : "Archive";
    }
  },
  validations() {
    return {
      formData: {
        first_name: { required, $autoDirty: true },
        last_name: { required, $autoDirty: true }
      }
    };
  },
  methods: {
    ...mapActions([
      "updateUser",
      "updateCustomerArchiveStatus",
      "confirmChanges"
    ]),
    handleSave() {
      this.v$.$validate().then((valid) => {
        if (valid) {
          this.saving = true;
          // Remove UI only keys
          const emails = this.cleanData(this.formData.emails);
          const phones = this.cleanData(this.formData.phones);
          // Remove email and phone from formData
          // eslint-disable-next-line no-unused-vars
          const { email, phone, ...customerAttributes } = this.formData;
          const data = {
            user: {
              ...customerAttributes,
              emails,
              phones
            }
          };
          this.updateUser({
            id: this.customer.id,
            data
          })
            .then(() => {
              window.notify.success("User updated successfully!");
              this.$emit("hide-modal");
              this.$emit("save-success");
            })
            .finally(() => (this.saving = false));
        }
      });
    },
    handleArchiveConfirmed() {
      this.confirmArchiveModalOpen = false;

      this.handleArchiveAction();
    },
    cleanData(data) {
      return data.map((item) => {
        if (item.new) {
          delete item.id;
          delete item.new;
        }
        return item;
      });
    },
    handleArchiveClicked() {
      if (!this.customer.archived && this.formData.has_subs) {
        this.confirmArchiveModalOpen = true;
        return;
      }

      if (this.customer.archived && this.formData.parent?.archived) {
        this.confirmUnarchiveModalOpen = true;
        return;
      }

      this.handleArchiveAction();
    },
    handleArchiveAction() {
      this.loadingAction = true;
      const action_url =
        this.customer.links.customer_archive ||
        this.customer.links.customer_unarchive;
      this.updateCustomerArchiveStatus({
        action_url,
        update_current_user: true
      })
        .then(() => {
          const performingAction = this.customer.archived
            ? "archived"
            : "unarchived";
          const fullName = `${this.customer.first_name} ${this.customer.last_name}`;
          let successMsg = `Customer ${fullName} was successfully ${performingAction}.`;
          if (!this.customer.archived) {
            successMsg +=
              " <br/><br/>Unarchiving customers may cause duplicate accounting keys.";
          }
          window.notify.success(successMsg);
          this.$emit("hide-modal");
          EventBus.$emit("refresh-customer-table");
        })
        .finally(() => {
          this.loadingAction = false;
        });
    }
  }
};
</script>
<style lang="scss">
.customer-edit-modal {
  .icon-margin-right {
    margin-right: calc(0.25rem + 25px);
  }
  .icon-margin-right-2x {
    margin-right: calc(0.5rem + 50px);
  }
  .scrollable-fields-container {
    max-height: 200px;
    overflow: scroll;
  }
}
</style>
